// URL ↔ board state encoder/decoder — FE-SCRM-008, FE-PINV-063.
//
// Filter state persists in URL. The URL encodes the active preset
// (if any), any ad-hoc filter modifications, the current pipeline
// view, and the Show-closed toggle.
//
// Encoding choice: each filter key is a single CSV param, matching
// what the BE accepts. The active preset rides ``preset``, the
// three-view router rides ``view``, the toolbar Show-closed toggle
// rides ``show_closed``. We never touch other query params on the
// page so the SalesCrmPage ``tab`` query param (which selects
// "pipeline") survives untouched.
//
// FE-PINV-063: the v1 ``geometry`` URL param is retired. ``view``
// supersedes it; ``?geometry=timeline`` bookmarks fall through the
// readBoardStateFromUrl branch and land on ``view: "grid"`` with
// the toolbar's view-toggle handler clearing the stale ``geometry``
// param on next state write.

import type {
  BoardFilters,
  CadenceHealth,
  LivesBand,
  OpportunityType,
} from "./types";
import { EMPTY_FILTERS } from "./types";


export type PipelineView = "grid" | "kanban" | "list";


const _PIPELINE_VIEWS: PipelineView[] = ["grid", "kanban", "list"];


export type GridMonthSpan = 6 | 12;


export interface RenewalBoardUrlState {
  filters: BoardFilters;
  preset_id: string | null;
  view: PipelineView;
  show_closed: boolean;
  /** FE-PINV-068. Grid column count. Default 6; toggle expands to 12. */
  grid_month_span: GridMonthSpan;
  /** FE-PINV-068. Months offset from "current calendar month" for the
   * leftmost visible Grid column. 0 = current month (default); -1 =
   * one month back; +1 = one month forward. The paddles in the Grid
   * header bump this value. */
  grid_start_offset: number;
}


const _LIVES_BAND_VALUES: LivesBand[] = [
  "micro", "small", "mid_market", "large", "enterprise",
];
const _OPP_TYPE_VALUES: OpportunityType[] = [
  "new_business", "bor_takeover", "rfp_response", "expansion",
];
const _CADENCE_VALUES: CadenceHealth[] = [
  "ahead", "on_track", "behind", "critical", "not_yet_urgent",
];


function readCsv<T extends string>(
  raw: string | null,
  allowed: readonly T[],
): T[] {
  if (!raw) return [];
  const allowedSet = new Set<string>(allowed);
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && allowedSet.has(s)) as T[];
}


function readFreeCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}


export function readBoardStateFromUrl(
  params: URLSearchParams,
): RenewalBoardUrlState {
  const filters: BoardFilters = {
    producer: readFreeCsv(params.get("producer")),
    broker_location: readFreeCsv(params.get("broker_location")),
    lives_band: readCsv(params.get("lives_band"), _LIVES_BAND_VALUES),
    industry: readFreeCsv(params.get("industry")),
    tags: readFreeCsv(params.get("tags")),
    opportunity_type: readCsv(
      params.get("opportunity_type"), _OPP_TYPE_VALUES,
    ),
    renewal_confidence: readFreeCsv(params.get("renewal_confidence")),
    cadence_health: readCsv(
      params.get("cadence_health"), _CADENCE_VALUES,
    ),
    has_active_alerts: params.get("has_active_alerts") === "true"
      ? true : null,
  };
  const preset_id = params.get("preset") || null;

  // FE-PINV-063: ?view drives the 3-view router. Invalid values
  // fall back to "grid". The retired v1 ``?geometry=timeline``
  // bookmark also lands on "grid" so old links don't 404.
  const viewRaw = params.get("view");
  const view: PipelineView = (
    viewRaw && (_PIPELINE_VIEWS as string[]).includes(viewRaw)
  ) ? (viewRaw as PipelineView) : "grid";

  const show_closed = params.get("show_closed") === "true";

  // FE-PINV-068: grid axis window — 6-month default, paddles ±1 month,
  // "Show 12 months" toggle expands the span. Per-saved-view persistence
  // rides on top of these URL keys via SavedView's columns_json.
  const span_raw = params.get("gridSpan");
  const grid_month_span: GridMonthSpan = span_raw === "12" ? 12 : 6;
  const offset_raw = params.get("gridOffset");
  const offset_num = offset_raw ? Number.parseInt(offset_raw, 10) : 0;
  const grid_start_offset = Number.isFinite(offset_num) ? offset_num : 0;

  return {
    filters, preset_id, view, show_closed,
    grid_month_span, grid_start_offset,
  };
}


export function writeBoardStateToParams(
  state: RenewalBoardUrlState,
  baseParams: URLSearchParams,
): URLSearchParams {
  // Mutate a copy so we don't smear over the caller's params (the
  // SalesCrmPage ``tab=`` param has to survive).
  const out = new URLSearchParams(baseParams);

  const setOrClear = (key: string, value: string) => {
    if (value) out.set(key, value);
    else out.delete(key);
  };

  setOrClear("producer", state.filters.producer.join(","));
  setOrClear("broker_location", state.filters.broker_location.join(","));
  setOrClear("lives_band", state.filters.lives_band.join(","));
  setOrClear("industry", state.filters.industry.join(","));
  setOrClear("tags", state.filters.tags.join(","));
  setOrClear(
    "opportunity_type", state.filters.opportunity_type.join(","),
  );
  setOrClear(
    "renewal_confidence", state.filters.renewal_confidence.join(","),
  );
  setOrClear("cadence_health", state.filters.cadence_health.join(","));
  if (state.filters.has_active_alerts === true) {
    out.set("has_active_alerts", "true");
  } else {
    out.delete("has_active_alerts");
  }

  if (state.preset_id) out.set("preset", state.preset_id);
  else out.delete("preset");

  // FE-PINV-063: write ``view`` only when not the default to keep
  // URLs short, and always clear the retired ``geometry`` param so
  // bookmarked v1 URLs don't keep round-tripping a stale value.
  if (state.view !== "grid") out.set("view", state.view);
  else out.delete("view");
  out.delete("geometry");

  if (state.show_closed) out.set("show_closed", "true");
  else out.delete("show_closed");

  if (state.grid_month_span === 12) out.set("gridSpan", "12");
  else out.delete("gridSpan");

  if (state.grid_start_offset !== 0) {
    out.set("gridOffset", String(state.grid_start_offset));
  } else {
    out.delete("gridOffset");
  }

  return out;
}


/** FE-PINV-068. Compute the `start_month` YYYY-MM string from
 * "current calendar month" plus the producer's paddle offset.
 * `today` is injectable so tests don't drift over time. */
export function computeGridStartMonth(
  offset: number, today: Date = new Date(),
): string {
  const idx = today.getUTCFullYear() * 12 + today.getUTCMonth() + offset;
  const year = Math.floor(idx / 12);
  const month = (idx % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}


// Apply a preset's filter_json to a BoardFilters. ``dynamic`` markers
// (per BE-SCRM-007) need substitution: "renewing_next_quarter" maps
// to a renewal_month-range filter computed at click time; "my_book"
// substitutes the producer's id (passed in by the caller).
export interface PresetApplyContext {
  current_user_membership_id?: string;
  today: Date;
}


export function applyPresetFilters(
  filter_json: Record<string, unknown>,
  ctx: PresetApplyContext,
): BoardFilters {
  const out: BoardFilters = { ...EMPTY_FILTERS };
  const dyn = (filter_json as { dynamic?: string }).dynamic;

  if (dyn === "my_book") {
    if (ctx.current_user_membership_id) {
      out.producer = [ctx.current_user_membership_id];
    }
  } else if (dyn === "renewing_next_quarter") {
    // Spec marks this as "computed dynamically at request time".
    // We surface it via cadence_health agnostic filtering — the
    // server-side renewal_month filter isn't a board param yet,
    // so for v1 the chip just narrows by cadence_health=behind/critical
    // (most of the next-three-month set will be in those buckets).
    // A precise renewal-month range can ride client-side filtering
    // once the BE adds the range param; this keeps the chip useful
    // without a contract change.
    out.cadence_health = ["behind", "critical"];
  } else {
    // Static filter set — copy known keys.
    const f = filter_json as Partial<BoardFilters>;
    if (Array.isArray(f.producer)) out.producer = f.producer as string[];
    if (Array.isArray(f.broker_location)) {
      out.broker_location = f.broker_location as string[];
    }
    if (Array.isArray(f.lives_band)) out.lives_band = f.lives_band as LivesBand[];
    if (Array.isArray(f.industry)) out.industry = f.industry as string[];
    if (Array.isArray(f.tags)) out.tags = f.tags as string[];
    if (Array.isArray(f.opportunity_type)) {
      out.opportunity_type = f.opportunity_type as OpportunityType[];
    }
    if (Array.isArray(f.renewal_confidence)) {
      out.renewal_confidence = f.renewal_confidence as string[];
    }
    if (Array.isArray(f.cadence_health)) {
      out.cadence_health = f.cadence_health as CadenceHealth[];
    }
    if (typeof f.has_active_alerts === "boolean") {
      out.has_active_alerts = f.has_active_alerts;
    }
  }
  return out;
}


// Compare two BoardFilters for "modified preset" indicator. Per spec:
// "When a preset is active and the producer adds or modifies a
// sidebar filter, the chip becomes visually 'modified'."
export function filtersEqual(
  a: BoardFilters, b: BoardFilters,
): boolean {
  const sameStrSet = (x: string[], y: string[]) => {
    if (x.length !== y.length) return false;
    const sx = new Set(x);
    return y.every((v) => sx.has(v));
  };
  return (
    sameStrSet(a.producer, b.producer)
    && sameStrSet(a.broker_location, b.broker_location)
    && sameStrSet(a.lives_band, b.lives_band)
    && sameStrSet(a.industry, b.industry)
    && sameStrSet(a.tags, b.tags)
    && sameStrSet(a.opportunity_type, b.opportunity_type)
    && sameStrSet(a.renewal_confidence, b.renewal_confidence)
    && sameStrSet(a.cadence_health, b.cadence_health)
    && a.has_active_alerts === b.has_active_alerts
  );
}
